import React, { useState, useEffect, useRef, useContext } from "react";
import {
  Box,
  Avatar,
  Typography,
  Tooltip,
  TextField,
  IconButton,
  Button,
} from "@mui/material";
import CommonService from "../../services/common/commonService";
import { clearCurrentSession, getUserAttributes } from "../../utils/auth";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import "../../styles/common.css";
import IntegrationService from "../../services/integrations/Integrations";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Settings from "../CalendarAvailability/Settings";
// Removed Amplify import - using custom logout
import LogoutIcon from '@mui/icons-material/Logout';
import useGlobalContext from "../../context/useGlobalContext";
import { GlobalContext } from "../../context/GlobalContext";
import { useUploadActions } from "../../context/UploadContext";
import { ImpersonationDetails } from "../../utils/types";
import { resolveDefaultRoute } from "../SideBar/SideBarConfig";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import ImpersonationIcon from "../../assets/common/impersonation-icon.svg";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { getAvatarInitials } from "../../utils/utils";
import DoneIcon from '@mui/icons-material/Done';
import impersonIcon from "../../assets/common/imperson-icon.svg";
interface ImpersonationProps {
  classes: any;
  setShowSpinner: (state: boolean) => void;
}

const Profile: React.FC<ImpersonationProps> = ({
  classes,
  setShowSpinner,
}) => {
  const userInfo = getUserAttributes();
  const navigate = useNavigate();
  const { showSnackbar } = useDisplaySnackbar();
  const [bookingUrl, setBookingUrl] = useState("");
  const [showBox, setShowBox] = useState<boolean>(false);
  const impersonationBoxRef = useRef<HTMLDivElement | null>(null);
  const { accountDetails } = useContext(GlobalContext);
  const isExternalUser = accountDetails?.isExternalUser ?? false;
  const [openSettings, setOpenSettings] = useState(false);
  const settingsBoxRef = useRef<HTMLDivElement | null>(null);
  const avatarRef = useRef<HTMLDivElement | null>(null);
  const [boxPos, setBoxPos] = useState<{ bottom: number; left: number }>({ bottom: 0, left: 0 });
  const [secondaryPos, setSecondaryPos] = useState<{ bottom: number; left: number }>({
  bottom: 30,
  left: 380,
});
  const [blockClicks, setBlockClicks] = useState(false);
  const { featureMap, impersonationReady, impersonationDetails, setImpersonationDetails } = useGlobalContext();
  const isCalendaeBookingsEnabled = featureMap?.calendar_booking === true;
  const isImpersonationEnabled = featureMap?.impersonation === true;
  const [email, setEmail] = useState("");
  const [activePopup, setActivePopup] = useState<string | null>(null);
  const [dropDownOptions, setDropDownOptions] = useState<ImpersonationDetails[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const accountId = accountDetails?.accountId;
  const employeePorId = accountDetails?.profileOrgId;
  const impersonationPopupRef = useRef<HTMLDivElement | null>(null);
  const [selectedImpersonationUser, setSelectedImpersonationUser] = useState<ImpersonationDetails | null>(null);
  const isImpersonating = impersonationDetails?.isImpersonating === true;
  const [searchText, setSearchText] = useState("");
  const [showSessionActive, setShowSessionActive] = useState(false);
  const selectWrapperRef = useRef<HTMLDivElement | null>(null);
  const filteredUsers = dropDownOptions.filter((user) =>
    user.impersonatingUserEmail
      ?.toLowerCase()
      .includes(searchText.toLowerCase())
  );
  const IMPERSONATION_POPUP_WIDTH = 360;
  const VIEWPORT_EDGE_GAP = 12;
  const {
    data: bookingData,
    isLoading: bookingIsLoading,
    isFetching: bookingIsFetching,
    isSuccess: bookingIsSuccess,
    isError: bookingIsError,
    error: bookingError,
  } = IntegrationService.useGetBookingUrl(isExternalUser? false : true);

  const { data: availabilityData, isLoading: availabilityIsLoading  } = IntegrationService.useGetCalendarAvailability(true);

  const updateSecondaryPos = () => {
    if (impersonationBoxRef.current) {
      const rect = impersonationBoxRef.current.getBoundingClientRect();
      const spaceLeft = rect.left - VIEWPORT_EDGE_GAP;
      const spaceRight = window.innerWidth - rect.right - VIEWPORT_EDGE_GAP;
      const shouldOpenLeft =
        spaceLeft >= IMPERSONATION_POPUP_WIDTH || spaceRight < IMPERSONATION_POPUP_WIDTH;
      const left = shouldOpenLeft
        ? rect.left - IMPERSONATION_POPUP_WIDTH - VIEWPORT_EDGE_GAP
        : rect.right + VIEWPORT_EDGE_GAP;
      const clampedLeft = Math.max(
        VIEWPORT_EDGE_GAP,
        Math.min(left, window.innerWidth - IMPERSONATION_POPUP_WIDTH - VIEWPORT_EDGE_GAP)
      );
      const clampedBottom = Math.max(
        VIEWPORT_EDGE_GAP,
        Math.min(
          window.innerHeight - rect.bottom + 4,
          window.innerHeight - VIEWPORT_EDGE_GAP
        )
      );
      setSecondaryPos({
        bottom: clampedBottom,
        left: clampedLeft,
      });
    }
  };

  const {
    isLoading: isProfilesLoading,
    isFetching: isProfileFetching,
    isSuccess: isProfilesSuccess,
    isError: isProfilesError,
    error: profilesError,
    data: profliesData,
  } = CommonService.useGetImpersonateProfile({
    accountId: accountId ?? "",
    employeePorId: employeePorId ?? "",
  }, isExternalUser ? false: true);

  useEffect(() => {
    if(isProfilesLoading || isProfileFetching) {
      setShowSpinner(true);
    }

    if(isProfilesSuccess && profliesData) {
      setDropDownOptions(profliesData?.data || []);
    }

    if(isProfilesError) {
      showSnackbar("error", profilesError?.message)
    }
  },[isProfilesLoading, isProfileFetching, isProfilesSuccess, profliesData, isProfilesError, profilesError]);

  const hasAvailability = (): boolean => {
    try {
      if (!availabilityData) return false;
      const d = availabilityData.data ?? availabilityData;
      if (Array.isArray(d?.slots)) return d.slots.length > 0;
      if (typeof d?.slotCount === "number") return d.slotCount > 0;
      if (typeof d?.available === "boolean") return d.available;
      if (d && typeof d === "object") {
        return Object.keys(d).length > 0;
      }
      return false;
    } catch (e) {
      console.error("Error checking availability:", e);
      return false;
    }
  };

  useEffect(() => {
    if (bookingIsSuccess && bookingData) {
      setBookingUrl(bookingData.data?.calendarBookingUrl || "");
    }
    if (bookingIsError) {
      console.error("Error fetching booking URL:", bookingError);
    }
  }, [
    bookingIsSuccess,
    bookingData,
    bookingIsLoading,
    bookingIsFetching,
    bookingIsError,
    bookingError,
  ]);


  const handleCopyBookingUrl = () => {
    if (availabilityIsLoading) {
      showSnackbar("info", "Checking your calendar availability — please wait a moment.");
      return;
    }
    if (!hasAvailability()) {
      showSnackbar("info", "No availability found. Please set your calendar availability.");
      setOpenSettings(true);
      setShowBox(true);
      return;
    }
    if (bookingUrl) {
      navigator.clipboard.writeText(bookingUrl);
      showSnackbar("success", "Booking URL copied to clipboard");
      setShowBox(false);
      setOpenSettings(false);
      setActivePopup(null);
    }
    else{
      showSnackbar("error", "Booking URL is not available right now.");
    }
  };

  const handleAvatarClick = () => {
    if (avatarRef.current) {
      const rect = avatarRef.current.getBoundingClientRect();
      const offsetY = 0;  // vertical gap below avatar
      const offsetX = 72;  // adjust if you want to nudge horizontally
      const bottom = window.innerHeight - rect.top + offsetY; // position below avatar
      const left = rect.left + offsetX;  // align with avatar's left
      setBoxPos({ bottom, left });
    }
    setShowBox((prev) => !prev);
  };

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node | null;

    const clickedInsideMenu =
      impersonationBoxRef.current?.contains(target!) ?? false;

    const clickedInsideImpersonationPopup =
      impersonationPopupRef.current?.contains(target!) ?? false;

    const clickedInsideSettings = settingsBoxRef.current
      ? settingsBoxRef.current.contains(target!)
      : false;

    const clickedOnAvatar = avatarRef.current
      ? avatarRef.current.contains(target!)
      : false;

    if (!clickedInsideMenu && !clickedInsideImpersonationPopup && !clickedInsideSettings && !clickedOnAvatar) {
      setShowBox(false);
      setActivePopup(null);
      setOpenSettings(false);
      setDropdownOpen(false);
      resetImpersonationForm();
    }
  };

  const handleClick = () => {
    setOpenSettings(true);
    setShowBox(true);
    setActivePopup(null);
    updateSecondaryPos();
  }

  useEffect(() => {
    const storedImpersonation =
      sessionStorage.getItem("impersonationDetails");
    if (storedImpersonation) {
      try {
        const impersonationData = JSON.parse(storedImpersonation);

        setImpersonationDetails(impersonationData);

        setSelectedImpersonationUser(impersonationData);

        setEmail(impersonationData.impersonatingUserEmail || "");
        setShowSessionActive(true);
      } catch (error) {
        console.error(
          "Failed to parse impersonation session",
          error
        );

        sessionStorage.removeItem("impersonationDetails");
      }
    }
    setShowBox(false);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const resetImpersonationForm = () => {
    setEmail("");
    setSearchText("");
    setDropdownOpen(false);
    setSelectedImpersonationUser(null);
  };

  const handleStartImpersonation = () => {
    if (!selectedImpersonationUser) {
      return;
    }

    if (!featureMap?.impersonation) {
      showSnackbar("error", "Impersonation is not available.");
      return;
    }

    const impersonationData = {
      ...selectedImpersonationUser,
      isImpersonating: true,
    };

    setImpersonationDetails(impersonationData);

    sessionStorage.setItem(
      "impersonationDetails",
      JSON.stringify(impersonationData)
    );

    setShowSessionActive(true);
  };

  useEffect(() => {
    if (!impersonationReady) return;
    setActivePopup(null);
    setShowBox(false);
      navigate(
        resolveDefaultRoute(
          featureMap,
          userInfo.email?.endsWith("@highradius.com"),
          true
        ),
        { replace: true }
      );
  }, [impersonationReady]);

  const handleStopImpersonation = () => {
    sessionStorage.removeItem("impersonationDetails");
    window.location.reload();

    setImpersonationDetails(null);

    setSelectedImpersonationUser(null);
    setEmail("");
    setShowSessionActive(false);
    setActivePopup(null);
    setShowBox(false);
    setSearchText("");

    navigate(
      resolveDefaultRoute(
        featureMap,
        userInfo.email?.endsWith("@highradius.com"),
        false
      ),
      { replace: true }
    );
  };

  const { abortAllUploads } = useUploadActions();
  const handleLogout = async () => {
    if (blockClicks) return;

    setBlockClicks(true);
    setShowSpinner(true);
    try {
      await abortAllUploads();
      // 🔹 1. Call backend logout FIRST (token still valid here)
      await CommonService.logoutUser();

    } catch (e) {
      // Even if backend logout fails, we should still sign out from Cognito
      console.error("Backend logout failed:", e);
    } finally {
      try {
        // 🔹 2. Clear local state & storage
        localStorage.setItem("isSignedOutOrTokenExpired", JSON.stringify(true));
        localStorage.removeItem("TaskPreferences");
        localStorage.removeItem("businessOverviewFilters");
        sessionStorage.removeItem("meetings_date_range_filter");
        sessionStorage.removeItem("TaskManagementDueDateRange");
        sessionStorage.removeItem("TaskManagementTableState");
        sessionStorage.removeItem("impersonationDetails");
        sessionStorage.removeItem("customerColumnVisibility");
        sessionStorage.removeItem("customerFilters");
        sessionStorage.removeItem("customerActiveViewName");
        sessionStorage.removeItem("selectedView");
        sessionStorage.removeItem("selectedSpace");
        sessionStorage.removeItem("multipart-upload");
        sessionStorage.removeItem("mmna_pipeline_columnOrder");
        clearCurrentSession();

        setShowBox(false);

        // 🔹 3. Custom logout - clear session and redirect
        localStorage.setItem("isSignedOutOrTokenExpired", JSON.stringify(true));
        window.location.href = '/login';
      } catch (e) {
        console.error("Custom logout failed:", e);
        setBlockClicks(false);
        setShowSpinner(false);
      }
    }
  };

  useEffect(() => {
    const handleClickOutsideDropdown = (
      event: MouseEvent
    ) => {
      const target = event.target as Node;

      if (
        dropdownOpen &&
        selectWrapperRef.current &&
        !selectWrapperRef.current.contains(target)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutsideDropdown
    );

    return () =>
      document.removeEventListener(
        "mousedown",
        handleClickOutsideDropdown
      );
  }, [dropdownOpen]);

  useEffect(() => {
    if (!showBox || activePopup !== "impersonation") return;
    updateSecondaryPos();
    const handleResize = () => updateSecondaryPos();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [showBox, activePopup]);

  return (
    <Box className="avatarContainer" sx={{ position: "relative", display: "inline-block" }}>
      <Avatar
        ref={avatarRef}
        alt="User Profile Image"
        src={userInfo?.profilePicture}
        className={classes.userAvatar}
        onClick={handleAvatarClick}
      />
      {showBox && (
        <Box className="impersonationBox" ref={impersonationBoxRef}
        sx={{ position: "fixed", zIndex: 100, bottom: `${boxPos.bottom}px`, left: `${boxPos.left}px`, minWidth: 280 }}
        >
          <Box className="sidebar-header">
            <Typography className="sidebar-name">{userInfo?.name}</Typography>
                <Typography className="sidebar-email">{userInfo?.email}</Typography>
          </Box>
          <Box className="sidebar-options">
            {isCalendaeBookingsEnabled && !isImpersonating && (
              <>
              <Tooltip title="Copy Booking URL" arrow placement="right">
                <Box className="sidebar-option" onClick={handleCopyBookingUrl}>
                  <ContentCopyIcon fontSize="small" className="sidebar-icon" />
                  <Typography className="sidebar-label">Calendar Booking Link</Typography>
                </Box>
              </Tooltip>
                <Box className="sidebar-option" onClick={handleClick}>
                  <AccessTimeIcon fontSize="small" className="sidebar-icon" />
                  <Typography className="sidebar-label">Calendar Availability Settings</Typography>
                </Box>
              </>
            )}
            {isImpersonationEnabled &&
              <Box
                onClick={() =>
                  { setOpenSettings(false);
                    setActivePopup("impersonation"); 
                    setShowBox(true);
                    updateSecondaryPos();
                  }
                }
                className={`sidebar-option ${
                  activePopup === "impersonation"
                    ? "active-option"
                    : ""
                }`}
              >
                <img src={ImpersonationIcon} className="sidebar-icon" />
                <Typography className="sidebar-label">Impersonation</Typography>
              </Box>}
            <Box className="sidebar-option" onClick={handleLogout}
            sx={{
              pointerEvents: blockClicks ? "none" : "auto",
              opacity: blockClicks ? 0.5 : 1,
              cursor: blockClicks ? "not-allowed" : "pointer",
            }}
            >
              <LogoutIcon fontSize="small" className="sidebar-icon"/>
              <Typography className="sidebar-label">Log out</Typography>
            </Box>
          </Box>
          {/* Secondary popup to the left of impersonationBox */}
        </Box>
      )}
      {openSettings && (
      <Box className="settings-popup" ref={settingsBoxRef} onMouseDown={(e) => e.stopPropagation()} sx={{
      position: "fixed",
      zIndex: 110,
      bottom: `${secondaryPos.bottom}px`,
      left: `${secondaryPos.left}px`,
      minWidth: 300,
    }}>
        <Settings onClose={() => setOpenSettings(false)}/>
      </Box>
    )}
    {activePopup && (
      <Box
      ref={impersonationPopupRef}
      className="impersonation-container"
      sx={{
        position: "fixed",
        zIndex: 110,
        bottom: `${secondaryPos.bottom}px`,
        left: `${secondaryPos.left}px`,
        minWidth: 360,
      }}
      >
        {activePopup === "impersonation" && 
        isImpersonating && showSessionActive ? (
          <Box className="impersonation-success-container">
            <Box className="impersonation-success-icon">
              <DoneIcon fontSize="small"/>
              <Box className="impersonation-success-badge">
                <img
                  src={impersonIcon}
                  alt="impersonation"
                  className="impersonation-success-badge-icon"
                />
              </Box>
            </Box>
            <Typography className="impersonation-success-title">
              Session Active
            </Typography>
            <Typography className="impersonation-success-subtitle">
              You are now acting as
            </Typography>
            <Box className="impersonation-success-email">
              {impersonationDetails?.impersonatingUserEmail}
            </Box>
            <Button
              className="impersonation-stop-session-btn"
              onClick={handleStopImpersonation}
            >
              <CloseIcon className="impersonation-stop-close-icon"/>
              Stop Impersonating
            </Button>
            <Button
              className="impersonation-close-btn"
              onClick={() => {setActivePopup(null); setShowBox(false); }}
            >
              Close
            </Button>
          </Box>
        ) : (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              mb={2}
            >
              <Box display="flex" gap={1}>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: "#6F4EF2",
                  }}
                >
                  <img src={ImpersonationIcon} style={{
                      width: 16,
                      height: 16,
                      filter: "brightness(0) invert(1)",
                    }}/>
                </Avatar>

                <Box>
                  <Typography fontWeight={600}>
                    Impersonation
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Temporarily access as another user
                  </Typography>
                </Box>
              </Box>

              <IconButton
                size="small"
                onClick={() => {
                  resetImpersonationForm();
                  setActivePopup(null);
                }}
                sx={{ backgroundColor:"#F2F2F4", ":hover": { backgroundColor: "#F2F2F4" } }}
              >
                <CloseIcon sx={{fontSize: 12,}}/>
              </IconButton>
            </Box>
            <Box ref={selectWrapperRef}className="impersonation-dropdown-wrapper">
              {dropdownOpen && (
                <Box className="impersonation-dropdown">
                  <Box className="impersonation-users">
                    {filteredUsers.map((user, index) => (
                      <Box
                        key={user.impersonatingUserEmail}
                        className="impersonation-user"
                        onClick={() => {
                          setSelectedImpersonationUser(user);
                          setEmail(user.impersonatingUserEmail);
                          setSearchText(user.impersonatingUserEmail);
                          setDropdownOpen(false);
                        }}
                      >
                        <Box
                          className="impersonation-user-dot"
                          sx={{
                            backgroundColor: [
                              "#6366F1",
                              "#0EA5E9",
                              "#10B981",
                              "#F59E0B",
                              "#EF4444",
                              "#8B5CF6",
                              "#EC4899",
                            ][index % 7],
                          }}
                        />

                        <Typography className="impersonation-user-email">
                          {user.impersonatingUserEmail}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              <TextField
                fullWidth
                value={searchText}
                placeholder="Search email..."
                onClick={() => setDropdownOpen(true)}
                className={`impersonation-select
                  ${dropdownOpen ? "impersonation-select-open" : ""}
                  ${selectedImpersonationUser ? "impersonation-select-selected" : ""}
                `}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchText(value);
                  setDropdownOpen(true);
                  if (selectedImpersonationUser) {
                    setSelectedImpersonationUser(null);
                    setEmail("");
                  }
                }}
                InputProps={{
                  endAdornment: email ? (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        resetImpersonationForm();
                      }}
                      sx={{ backgroundColor:"#E0E7FF" }}
                    >
                      <CloseIcon
                        sx={{
                          fontSize: 12,
                          color: "#7C86FF",
                        }}
                      />
                    </IconButton>
                  ) : (
                    <KeyboardArrowDownIcon
                      sx={{
                        color: dropdownOpen ? "#8B5CF6" : "#667085",
                        fontSize: 18,
                      }}
                    />
                  ),
                }}
              />
            </Box>

            {selectedImpersonationUser && (
              <Box className="impersonation-selected-user">
                <Avatar className="impersonation-selected-avatar">
                  {getAvatarInitials(
                    selectedImpersonationUser.impersonatingUserEmail
                  )}
                </Avatar>

                <Box>
                  <Typography className="impersonation-selected-label">
                    Impersonating
                  </Typography>

                  <Typography className="impersonation-selected-email">
                    {selectedImpersonationUser.impersonatingUserEmail}
                  </Typography>
                </Box>
              </Box>
            )}

            <Box className="impersonation-footer">
              <Button
                variant="outlined"
                className="impersonation-cancel-btn"
                onClick={() => {
                  resetImpersonationForm();
                  setActivePopup(null);
                }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                className="impersonation-start-btn"
                disabled={!selectedImpersonationUser}
                onClick={handleStartImpersonation}
              >
                <img
                  src={impersonIcon}
                  alt="impersonation"
                  className="impersonation-start-icon"
                />
                Start Impersonation
              </Button>
            </Box>
          </>
        )}
      </Box>
    )}
    </Box>
  );
};

export default Profile;
